import apiClient from './apiClient';

class UserService {
  async execute(method, resource, data, config = {}) {
    const requestConfig = {
      method,
      url: resource,
      ...config, // Merge config (includes signal for cancellation)
    };
    
    if (data) {
      if (method === 'get' || method === 'delete') {
        requestConfig.params = data;
      } else {
        requestConfig.data = data;
      }
    }
    
    try {
      const response = await apiClient(requestConfig);
      return response;
    } catch (error) {
      // Don't throw error if request was canceled
      if (error.name === 'AbortError' || error.message === 'canceled' || error.code === 'ECONNABORTED') {
        throw error; // Re-throw to let component handle it
      }
      throw error;
    }
  }

  // Address operations
  getAllAddress() {
    return this.execute("get", "/address/");
  }

  updateAddress(id, payload) {
    return this.execute("put", `/address/${id}/`, payload);
  }

  deleteAddress(id) {
    return this.execute("delete", `/address/${id}/`);
  }

  addAddress(payload) {
    return this.execute("post", "/address/", payload);
  }

  // Product operations
  getProducts(params) {
    return this.execute(
      "get",
      `/product/?first_letter=${params.first_letter}&page=${params.page}`
    );
  }

  getOtcByID(id) {
    return this.execute("get", `/otc/${id}/`);
  }

  getProductByID(id) {
    return this.execute("get", `/product/${id}/`);
  }

  getSameSaltProducts(productId) {
    return this.execute("get", `/product/${productId}/same-salt-composition/`);
  }

  getSameCategoryOTCs(otcId) {
    return this.execute("get", `/otc/${otcId}/same-category/`);
  }

  // Cart operations
  addCart(id) {
    return this.execute("post", `/cart/add/${id}/`);
  }

  addOTCCart(id) {
    return this.execute("post", `/cart/otc/${id}/`);
  }

  removeOCart(id) {
    return this.execute("put", `/cart/ored/${id}/`);
  }

  removePCart(id) {
    return this.execute("put", `/cart/pred/${id}/`);
  }

  cartCount() {
    return this.execute("get", "/cart/count/");
  }

  getCart() {
    return this.execute("get", "/ucart/");
  }

  deletePCart(id) {
    return this.execute("delete", `/delete/product/${id}/`);
  }

  deleteOCart(id) {
    return this.execute("delete", `/delete/otc/${id}/`);
  }

  // Checkout
  checkOut() {
    return this.execute("post", `/cart/checkout/`);
  }

  // Razorpay payment
  createRazorpayOrder() {
    return this.execute("post", `/payment/create-order/`);
  }

  verifyRazorpayPayment(payload) {
    return this.execute("post", `/payment/verify/`, payload);
  }

  // Dev bypass — skip Razorpay, mark payment as completed (DEBUG only)
  devBypassPayment(payload) {
    return this.execute("post", `/payment/dev-bypass/`, payload);
  }

  // Prescription upload
  uploadPrescription(payload, config = {}) {
    return this.execute("post", `/prescriptions/`, payload, {
      headers: { 'Content-Type': 'multipart/form-data' },
      ...config
    });
  }

  // Membership operations
  getMemberships() {
    return this.execute("get", "/memberships/");
  }

  getUserMemberships() {
    return this.execute("get", "/user-memberships/");
  }

  createMembershipRazorpayOrder(payload) {
    return this.execute("post", "/memberships/create-order/", payload);
  }

  verifyMembershipRazorpayPayment(payload) {
    return this.execute("post", "/memberships/verify/", payload);
  }

  // OTC operations - Advanced filtering with POST
  getOtcProducts(params) {
    let payload = {};
    
    // Handle category filter - ensure it's an array of category names
    if (params.category && Array.isArray(params.category) && params.category.length > 0) {
      payload.categories = params.category.map(cat => {
        // Extract name from category object or use string directly
        return typeof cat === 'object' ? cat.name : cat;
      });
    }
    
    // Handle manufacturer filter - ensure it's an array
    if (params.manufacturer && Array.isArray(params.manufacturer) && params.manufacturer.length > 0) {
      payload.manufacturers = params.manufacturer;
    }
    
    // Handle sub-categories filter
    if (params.sub_categories && Array.isArray(params.sub_categories) && params.sub_categories.length > 0) {
      payload.sub_categories = params.sub_categories;
    }
    
    // Handle name search
    if (params.name) {
      payload.name = params.name;
    }
    
    // Handle price range
    if (params.min_price) {
      payload.min_price = params.min_price;
    }
    if (params.max_price) {
      payload.max_price = params.max_price;
    }
    
    // Handle ordering/sorting
    if (params.ordering) {
      payload.ordering = params.ordering;
    }
    
    // Include pagination via query string as backend expects ?page=N on /otc/filter/
    const pageQuery = params.page ? `?page=${encodeURIComponent(params.page)}` : '';
    return this.execute("post", `/otc/filter/${pageQuery}`, payload);
  }

  // Alternative GET method for OTC products with query parameters
  getOtcProductsGET(params) {
    const queryParams = new URLSearchParams();
    
    if (params.page) {
      queryParams.append('page', params.page);
    }
    
    if (params.search) {
      queryParams.append('search', params.search);
    }
    
    if (params.name) {
      queryParams.append('name', params.name);
    }
    
    if (params.category) {
      queryParams.append('category', params.category);
    }
    
    if (params.manufacturer) {
      queryParams.append('manufacturer', params.manufacturer);
    }
    
    if (params.sub_category) {
      queryParams.append('sub_category', params.sub_category);
    }
    
    if (params.categories && Array.isArray(params.categories)) {
      queryParams.append('categories', params.categories.join(','));
    }
    
    if (params.manufacturers && Array.isArray(params.manufacturers)) {
      queryParams.append('manufacturers', params.manufacturers.join(','));
    }
    
    if (params.sub_categories && Array.isArray(params.sub_categories)) {
      queryParams.append('sub_categories', params.sub_categories.join(','));
    }
    
    if (params.min_price) {
      queryParams.append('min_price', params.min_price);
    }
    
    if (params.max_price) {
      queryParams.append('max_price', params.max_price);
    }
    
    if (params.ordering) {
      queryParams.append('ordering', params.ordering);
    }
    
    // Use correct endpoint: GET /otc/
    const url = queryParams.toString() ? `/otc/?${queryParams.toString()}` : '/otc/';
    return this.execute("get", url);
  }

  getCategories() {
    return this.execute("get", "/otc/categories/");
  }

  getManufacturer(page = 1, pageSize = 200) {
    return this.execute("get", `/manufacturers/?page=${page}&page_size=${pageSize}`);
  }

  // Filter Options APIs
  getFilterOptions(categoryId = null, category = null) {
    let url = "/otc/filter-options/";
    const params = new URLSearchParams();
    
    if (categoryId) {
      params.append('category_id', categoryId);
    }
    if (category) {
      params.append('category', category);
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    return this.execute("get", url);
  }

  getManufacturersByCategories(categories) {
    return this.execute("post", "/otc/manufacturers/", { categories });
  }

  getManufacturers(categoryId = null, category = null) {
    let url = "/otc/manufacturers/";
    const params = new URLSearchParams();
    
    if (categoryId) {
      params.append('category_id', categoryId);
    }
    if (category) {
      params.append('category', category);
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    return this.execute("get", url);
  }

  getSubCategoriesByCategories(categories) {
    return this.execute("post", "/otc/sub_categories/", { category: categories });
  }

  // Category-specific product APIs
  getProductsByCategory(categoryName, params = {}) {
    const queryParams = new URLSearchParams();
    
    queryParams.append('category', categoryName);
    queryParams.append('page', params.page || 1);
    
    // Add discount range if provided
    if (params.min_discount !== undefined) {
      queryParams.append('min_discount', params.min_discount);
    }
    if (params.max_discount !== undefined) {
      queryParams.append('max_discount', params.max_discount);
    }
    
    // Add ordering if provided
    if (params.ordering) {
      queryParams.append('ordering', params.ordering);
    }
    
    return this.execute("get", `/otc/?${queryParams.toString()}`);
  }

  // Discount range product APIs
  getProductsByDiscountRange(minDiscount, maxDiscount, params = {}, config = {}) {
    const queryParams = new URLSearchParams();
    
    queryParams.append('min_discount', minDiscount);
    queryParams.append('max_discount', maxDiscount);
    queryParams.append('page', params.page || 1);
    
    // Add category filter if provided
    if (params.category) {
      queryParams.append('category', params.category);
    }
    
    // Add ordering if provided
    if (params.ordering) {
      queryParams.append('ordering', params.ordering);
    }
    
    // Pass config (including signal) to execute method
    return this.execute("get", `/otc/?${queryParams.toString()}`, null, config);
  }

  // Get discount range statistics
  getDiscountRangeStats() {
    return this.execute("get", "/otc/discount-range/");
  }

  // Orders
  getOrders() {
    return this.execute("get", "/orders/");
  }

  getOrderbyId(id) {
    return this.execute("get", `/orders/${id}/`);
  }

  orderToCart(id) {
    return this.execute("post", `/orders/${id}/add-to-cart/`);
  }

  // Search
  searchBar(str) {
    return this.execute("get", `/product/?search=${str}`);
  }

  // Promotional Content APIs
  getAllPromotionalContent() {
    return this.execute("get", "/promotional-content/");
  }

  getAdCarousels(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/ad-carousels/?${queryString}` : "/ad-carousels/";
    return this.execute("get", url);
  }

  getTrendingProducts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/trending-products/?${queryString}` : "/trending-products/";
    return this.execute("get", url);
  }

  getDiscountProducts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/discount-products/?${queryString}` : "/discount-products/";
    return this.execute("get", url);
  }

  getBestSellers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/best-sellers/?${queryString}` : "/best-sellers/";
    return this.execute("get", url);
  }

  getNewArrivals(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/new-arrivals/?${queryString}` : "/new-arrivals/";
    return this.execute("get", url);
  }

  incrementTrendingProductView(productId) {
    return this.execute("post", `/trending-products/${productId}/increment-view/`);
  }

  purchaseMembership(membershipId) {
    return this.execute("post", "/user-memberships/purchase/", {
      membership_id: membershipId
    });
  }

  getPopularCategories(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/popular-categories/?${queryString}` : "/popular-categories/";
    return this.execute("get", url);
  }

  getFeaturedBrands(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/featured-brands/?${queryString}` : "/featured-brands/";
    return this.execute("get", url);
  }

  getExploreBeauty(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/explore-beauty/?${queryString}` : "/explore-beauty/";
    return this.execute("get", url);
  }

  getCelebrationSpecials(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/celebration-specials/?${queryString}` : "/celebration-specials/";
    return this.execute("get", url);
  }

  getShopByHealthConcerns(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/shop-by-health-concerns/?${queryString}` : "/shop-by-health-concerns/";
    return this.execute("get", url);
  }

  // Pincode Search APIs
  checkPincode(pincode) {
    return this.execute("post", "/pincodes/search/", { pincode });
  }

  checkPincodeGET(pincode) {
    return this.execute("get", `/pincodes/check/?pincode=${pincode}`);
  }

  validatePincodeFormat(pincode) {
    const errors = [];
    
    if (!pincode) {
      errors.push("Pincode is required");
    } else if (!/^\d{6}$/.test(pincode)) {
      errors.push("Pincode must be exactly 6 digits");
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  formatPincodeInput(value) {
    // Only allow digits
    const digitsOnly = value.replace(/\D/g, '');
    
    // Limit to 6 characters
    const limited = digitsOnly.substring(0, 6);
    
    return limited;
  }

  // User Profile operations
  getUserProfile() {
    return this.execute("get", "/user-profile/");
  }

  getUserProfileById(userId) {
    return this.execute("get", `/user-profile/${userId}/`);
  }

  updateUserProfile(userId, payload) {
    return this.execute("put", `/user-profile/${userId}/`, payload);
  }

  patchUserProfile(userId, payload) {
    return this.execute("patch", `/user-profile/${userId}/`, payload);
  }
}

export default new UserService();
